import 'react-quill/dist/quill.snow.css'

import { cn } from '@repo/tailwind-config/tailwind.functions'
import { StringFormatter } from '@repo/toolkit/string'
import { useMemo } from 'react'
import ReactQuill from 'react-quill'

import Typography from './typography'

const ALLOWED_RICH_TEXT_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'I',
  'LI',
  'OL',
  'P',
  'PRE',
  'S',
  'SPAN',
  'STRIKE',
  'STRONG',
  'SUB',
  'SUP',
  'U',
  'UL'
])

const BLOCKED_RICH_TEXT_TAGS = new Set([
  'IFRAME',
  'SCRIPT',
  'STYLE',
  'OBJECT',
  'EMBED',
  'LINK',
  'META'
])

const ALLOWED_STYLE_PROPERTIES = new Set([
  'background-color',
  'color',
  'font-family',
  'font-size',
  'text-align'
])

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

const isSafeUrl = (value: string) => {
  try {
    const url = new URL(value, window.location.origin)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)
  } catch {
    return false
  }
}

const sanitizeStyle = (style: string) =>
  style
    .split(';')
    .map((rule) => {
      const [property, ...rawValue] = rule.split(':')
      if (!property || rawValue.length === 0) return null

      const normalizedProperty = property.trim().toLowerCase()
      const value = rawValue.join(':').trim()
      const normalizedValue = value.toLowerCase()

      if (!ALLOWED_STYLE_PROPERTIES.has(normalizedProperty)) return null
      if (
        normalizedValue.includes('url(') ||
        normalizedValue.includes('expression(') ||
        normalizedValue.includes('javascript:')
      )
        return null

      return `${normalizedProperty}: ${value}`
    })
    .filter(Boolean)
    .join('; ')

const sanitizeReadonlyRichText = (value?: string) => {
  if (!value) return value
  if (typeof document === 'undefined') return escapeHtml(value)

  const template = document.createElement('template')
  template.innerHTML = value

  const sanitizeNode = (node: Node): void => {
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const element = node as HTMLElement

    if (BLOCKED_RICH_TEXT_TAGS.has(element.tagName)) {
      element.remove()
      return
    }

    Array.from(element.childNodes).forEach(sanitizeNode)

    if (!ALLOWED_RICH_TEXT_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes))
      return
    }

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase()
      const attributeValue = attribute.value

      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name)
        return
      }

      if (name === 'href' && element.tagName === 'A') {
        if (!isSafeUrl(attributeValue)) {
          element.removeAttribute(attribute.name)
          return
        }

        element.setAttribute('target', '_blank')
        element.setAttribute('rel', 'noopener noreferrer')
        return
      }

      if (name === 'class') {
        const safeClasses = attributeValue
          .split(/\s+/)
          .filter((className) => className.startsWith('ql-'))
          .join(' ')

        if (safeClasses) element.setAttribute('class', safeClasses)
        else element.removeAttribute(attribute.name)
        return
      }

      if (name === 'style') {
        const sanitizedStyle = sanitizeStyle(attributeValue)
        if (sanitizedStyle) element.setAttribute('style', sanitizedStyle)
        else element.removeAttribute(attribute.name)
        return
      }

      if (!['target', 'rel'].includes(name)) element.removeAttribute(attribute.name)
    })
  }

  Array.from(template.content.childNodes).forEach(sanitizeNode)

  return template.innerHTML
}

export interface TextEditorProps {
  /**
   * placeholder of the editor when empty value
   */
  placeholder?: string

  /**
   * value of the editor
   */
  value?: string

  /*
   * The error message that will be displayed below the rich text editor
   */
  errorMessage?: string

  /*
   * is the editor in view or edit mode
   */
  readOnly?: boolean

  /**
   * If true, we display a '*' after the placeholder
   */
  required?: boolean

  /**
   * handle change event
   */
  onChange?: (value: string) => void

  /**
   * Callback to handle the input blur
   */
  onBlur?: () => void

  /**
   * If true, hides the toolbar controls
   */
  hideControls?: boolean
}

const font = ReactQuill.Quill.import('attributors/style/font') as {
  whitelist: string[]
}
font.whitelist = ['sans-serif', 'serif', 'monospace', 'Roboto', 'Inter', 'Pacifico']

ReactQuill.Quill.register(font, true)

const TextEditor = ({
  errorMessage,
  hideControls,
  readOnly,
  value,
  ...props
}: TextEditorProps) => {
  const sanitizedReadonlyValue = useMemo(
    () => (readOnly ? sanitizeReadonlyRichText(value) : value),
    [readOnly, value]
  )

  const modules = useMemo(
    () => ({
      toolbar: hideControls
        ? false
        : {
            container: [
              // please check the global.css file in the tailwind config when you change the order
              // since we are using css child selectors to style the toolbar
              [{ font: font.whitelist }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [
                'bold',
                'italic',
                'underline',
                'strike',
                // keep the color and background color as {key: []} since they are a dropdown
                { color: [] },
                { background: [] },
                { script: 'sub' },
                { script: 'super' },
                { list: 'ordered' },
                { list: 'bullet' },
                { indent: '-1' },
                { indent: '+1' },
                'align',
                'link',
                'clean'
              ]
            ],
            clipboard: {
              matchVisual: false
            },
            history: {
              delay: 500,
              maxStack: 100,
              userOnly: true
            }
          }
    }),
    [hideControls]
  )

  return (
    <div className={'flex flex-col gap-2'}>
      <ReactQuill
        {...props}
        readOnly={readOnly}
        value={sanitizedReadonlyValue}
        placeholder={StringFormatter.isRequired(props.placeholder ?? '', props.required)}
        theme={'snow'}
        modules={modules}
        className={cn('bg-white rounded-lg border border-grey-light', {
          'border-red': errorMessage
        })}
      />
      {errorMessage ? (
        <Typography variant={'small'} className={'text-red '}>
          {errorMessage}
        </Typography>
      ) : null}
    </div>
  )
}

export default TextEditor
